import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text, Image, Heading } from 'rebass';
import { useWindowSize } from 'react-use';
import { BsCheckCircleFill } from "react-icons/bs";
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

import duck from './assets/Duck.png';
import helpSupport from 'app/navigation/sidebar/assets/HelpSupport.png';
import { PrimaryButton } from 'family-tree/components/FtButton';

export const FTUE_STORAGE_KEY = 'babysFirstYearFtueSeen';

const STEPS = { WELCOME: 0, PROFILE: 1, SUCCESS: 2 };

// First-time-user experience for the Talking Family Tree.
//  1. Welcome
//  2. "Before You Begin" — reuses collections/edit/child-journal (baby profile);
//     its save → onSuccess advances to step 3.
//  3. "Great! Next, create an invitation…" success.
// Shown as a modal on desktop, full-screen on mobile.
const Ftue = ({ onClose, onCreateInvitation }) => {
  const { width } = useWindowSize();
  const isDesktop = width >= 768;
  const [step, setStep] = useState(STEPS.WELCOME);

  const heading = (text, p) => (
    <Heading p={p} px={[3, 4]} fontFamily="Bitter" fontWeight={500} fontSize={"30"} color={"#000"} textAlign="left">
      {text}
    </Heading>
  );
  const sub = (text, p) => (
    <Text p={p} px={[3, 4]} fontFamily="Poppins" fontSize="16px" color="#64748B" textAlign="left" mt={2}>
      {text}
    </Text>
  );

  let content;
  if (step === STEPS.WELCOME) {
    content = (
      <Flex flexDirection="column">
        {heading('Welcome to your Book Layout', 2)}
        {sub(<>
          We’ve taken all of your story content and given it an initial layout. You have some flexibility to make changes.
          <br />
          <br />
          <b>In version 1 of your Book Layout you can:</b>
          <br />
          <ul style={{ listStyle: 'none', fontFamily: "Poppins", color: "#64748B", gap: '10px' }}>
            {[
              'Change page layouts',
              'Crop photos',
              'Edit your original story'
            ].map((c) => <li >
              <Flex alignItems="center">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M24 12C24 15.1826 22.7357 18.2348 20.4853 20.4853C18.2348 22.7357 15.1826 24 12 24C8.8174 24 5.76516 22.7357 3.51472 20.4853C1.26428 18.2348 0 15.1826 0 12C0 8.8174 1.26428 5.76516 3.51472 3.51472C5.76516 1.26428 8.8174 0 12 0C15.1826 0 18.2348 1.26428 20.4853 3.51472C22.7357 5.76516 24 8.8174 24 12ZM18.045 7.455C17.9379 7.34823 17.8103 7.26416 17.6699 7.20782C17.5295 7.15149 17.3792 7.12404 17.228 7.12712C17.0768 7.1302 16.9277 7.16375 16.7897 7.22575C16.6518 7.28776 16.5277 7.37695 16.425 7.488L11.2155 14.1255L8.076 10.9845C7.86274 10.7858 7.58067 10.6776 7.28922 10.6827C6.99776 10.6879 6.71969 10.8059 6.51357 11.0121C6.30745 11.2182 6.18938 11.4963 6.18424 11.7877C6.1791 12.0792 6.28728 12.3612 6.486 12.5745L10.455 16.545C10.5619 16.6517 10.6892 16.7358 10.8294 16.7923C10.9695 16.8487 11.1196 16.8764 11.2706 16.8736C11.4217 16.8708 11.5706 16.8376 11.7085 16.776C11.8465 16.7144 11.9706 16.6256 12.0735 16.515L18.0615 9.03C18.2656 8.81775 18.3784 8.53391 18.3756 8.23944C18.3728 7.94496 18.2546 7.66332 18.0465 7.455H18.045Z" fill="#219653" />
                </svg>
                <Text
                  ml={1}
                  color="#64748B"
                  textAlign="left"
                  fontFamily="Poppins"
                  fontSize={18}
                  lineHeight="165%"
                  letterSpacing="-0.165"
                >
                  {c}
                </Text>
              </Flex>
            </li>)}
          </ul>
          <b>What’s yet to come:</b>
          <br />
          <ul style={{ listStyle: 'none', fontFamily: "Poppins", color: "#64748B", gap: '10px' }}>
            {[
              'Moving media between pages',
            ].map((c) => <li >
              <Flex alignItems="center">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 22.5C9.21523 22.5 6.54451 21.3938 4.57538 19.4246C2.60625 17.4555 1.5 14.7848 1.5 12C1.5 9.21523 2.60625 6.54451 4.57538 4.57538C6.54451 2.60625 9.21523 1.5 12 1.5C14.7848 1.5 17.4555 2.60625 19.4246 4.57538C21.3938 6.54451 22.5 9.21523 22.5 12C22.5 14.7848 21.3938 17.4555 19.4246 19.4246C17.4555 21.3938 14.7848 22.5 12 22.5ZM12 24C15.1826 24 18.2348 22.7357 20.4853 20.4853C22.7357 18.2348 24 15.1826 24 12C24 8.8174 22.7357 5.76516 20.4853 3.51472C18.2348 1.26428 15.1826 0 12 0C8.8174 0 5.76516 1.26428 3.51472 3.51472C1.26428 5.76516 0 8.8174 0 12C0 15.1826 1.26428 18.2348 3.51472 20.4853C5.76516 22.7357 8.8174 24 12 24Z" fill="#219653" />
                </svg>
                <Text
                  ml={1}
                  color="#64748B"
                  textAlign="left"
                  fontFamily="Poppins"
                  fontSize={18}
                  lineHeight="165%"
                  letterSpacing="-0.165"
                >
                  {c}
                </Text>
              </Flex>
            </li>)}
          </ul>
        </>, 0)}
      </Flex>
    );
  } else {
    content = (
      <Flex flexDirection="column" alignItems="center" textAlign="center">
        <Image src={duck} alt="" width={182} mb={4} />
        {heading('If you have questions or feedback...')}
        <br />
        {/* {sub(<>
          Please access <Image src={helpSupport} /> in your sidebar
        </>)} */}
        <Box
          // as="span"
          textAlign="left"
          // alignItems="center"
          // justifyContent="flex-start"
          // flexWrap="wrap"
          px={[3, 4]}
        // width="100%"
        >
          <Text as="span" sx={{
            display: "inline", textAlign: "center",
            color: "#64748B",
            fontFamily: 'Poppins',
            fontSize: "18px",
            lineHeight: '165%',
            fontWeight: 500
          }}>
            Please access
            <Image
              src={helpSupport}
              alt="Help & Feedback"
              sx={{
                display: "inline-block",
                height: "45px",
                mr: '-25px',
                ml: '-5px',
                width: "auto",
                maxWidth: "100%",
                verticalAlign: "middle",
                flexShrink: 0,
              }}
            />
            in your sidebar
          </Text>


          {/* <Text as="span" sx={{ display: "inline" }}>
          </Text> */}
        </Box>
        <br />
        <Text px={[3, 4]} color="#64748B" textAlign="left" fontFamily="Poppins" fontSize="16px" fontStyle="italic">
          Your feedback also makes Baby Yearbooks better for all parents
        </Text>
      </Flex>
    );
  }

  const back = () => {
    if (step === STEPS.WELCOME) onClose();
    else setStep((s) => s - 1);
  };

  let primary;
  if (step === STEPS.WELCOME) {
    primary = <PrimaryButton onClick={() => setStep(STEPS.PROFILE)}>Next <FiChevronRight size={20} /></PrimaryButton>;
  } else {
    primary = (
      <PrimaryButton onClick={() => { onClose(); if (onCreateInvitation) onCreateInvitation(); }}>
        Sounds great
      </PrimaryButton>
    );
  }

  return (
    <Box
      css="position: fixed; inset: 0; z-index: 10000;"
      sx={{
        bg: isDesktop ? 'rgba(0,0,0,0.5)' : '#fff',
        display: 'flex',
        alignItems: isDesktop ? 'center' : 'stretch',
        justifyContent: 'center',
        // p: isDesktop ? 4 : 0,
        overflowY: 'auto',
      }}
    >
      <Flex
        flexDirection="column"
        bg="white"
        sx={{
          width: '100%',
          maxWidth: isDesktop ? '460px' : '100%',
          minHeight: isDesktop ? 'auto' : '100vh',
          maxHeight: isDesktop ? '92vh' : '100vh',
          borderRadius: isDesktop ? '16px' : 0,
          boxShadow: isDesktop ? 'high' : 'none',
          overflow: 'hidden',
        }}
      >

        <Box flex="1 1 auto" py={"20px"} pb={[8, 3]} css="overflow-y: auto;">
          {content}
        </Box>

        <Flex px={[3, 4]} py={3} alignItems="center" justifyContent={step !== STEPS.WELCOME ? "space-between" : 'flex-end'} sx={{
          borderTop: '1px solid',
          ...(!isDesktop && {
            position: 'fixed',
            bottom: '0',
            width: '100%',
            left: '0',
            background: 'white',
          })
        }}>
          {step !== STEPS.WELCOME && <Box as="button" type="button" onClick={back}
            sx={{ background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', fontFamily: 'Poppins', fontSize: '14px' }}>
            <FiChevronLeft size={16} /> Back
          </Box>}
          {primary}
        </Flex>
      </Flex>
    </Box>
  );
};

Ftue.propTypes = {
  onClose: PropTypes.func.isRequired,
  onCreateInvitation: PropTypes.func,
};

Ftue.defaultProps = {
  onCreateInvitation: null,
};

export default Ftue;
